import gsap from 'gsap';

import {
  mobileBackgroundPreBonusResources,
  mobileBackgroundBonusResources,
  mobileBackgroundResources,
  mobileBgAnimationNames,
} from '../../../core/mobileResources';
import { mobileBackgroundRssNames } from '../../../core/mobileResourceNames';
import { gameMobileConfig } from '../../../core/configs';
import { BaseBackgroundDrawer } from './BaseBackgroundDrawer';
import { getTranslation, translationKeys } from '../../../core/translation/translation';
import { LAST_RESULTS_BONUS, LAST_RESULTS } from '../../../stateManagment/gameState';
import {
  crystalTypes,
} from '../../../core/gameConstants';
import { balanceLastWinDrawer } from '../../balanceLastWin/BalanceLastWinDrawer';
import { FONT_LATO_NAME } from '../../../core/constats';
import { animationDrawer } from '../../../core/AnimationDrawer';
import { wheelDrawer } from '../../wheel/WheelDrawer';
import { soundService } from '../../../services/helperService/PixiSoundService';
import { iframeService } from '../../../services/IframeService';

export class BackgroundMobileDrawer extends BaseBackgroundDrawer {
  constructor() {
    super();
    this.container = null;
    this.resources = {};
    this.preBonusResources = null;
    this.bonusResources = null;
    this.mainSceneResources = null;
    this.startPosition = null;
    this.endPosition = null;
    this.loader = this.createLoader();
  }

  async loadResource() {
    if (!this.mainSceneResources) {
      const { res } = await super.loadResource(mobileBackgroundResources, this.loader);
      this.resources = {
        ...this.resources,
        ...res,
      };
      this.mainSceneResources = res;
    }
    return this.resources;
  }

  async loadPreBonusResource() {
    if (!this.preBonusResources) {
      const { res } = await super.loadResource(mobileBackgroundPreBonusResources, this.loader);
      this.resources = {
        ...this.resources,
        ...res,
      };
      this.preBonusResources = res;
    }
    return this.resources;
  }

  async loadBonusResource() {
    if (!this.bonusResources) {
      const { res } = await super.loadResource(mobileBackgroundBonusResources, this.loader);
      this.resources = {
        ...this.resources,
        ...res,
      };
      this.bonusResources = res;
    }
    return this.resources;
  }

  async onLoad() {
    const { res } = await super.loadResource([
      ...mobileBackgroundResources,
      ...mobileBackgroundPreBonusResources,
      ...mobileBackgroundBonusResources,
    ], this.loader);
    this.resources = {
      ...this.resources,
      ...res,
    };
  }

  __setAnimations(texture) {
    this.animations = {
      blue: (loop = false) => texture.state.setAnimation(0, mobileBgAnimationNames.blue, loop),
      glow: (loop = false) => texture.state.setAnimation(0, mobileBgAnimationNames.glow, loop),
      green: (loop = false) => texture.state.setAnimation(0, mobileBgAnimationNames.green, loop),
      magenta: (loop = false) => texture.state.setAnimation(0, mobileBgAnimationNames.magenta, loop),
      yellow: (loop = false) => texture.state.setAnimation(0, mobileBgAnimationNames.yellow, loop),
      gold: (loop = false) => texture.state.setAnimation(0, mobileBgAnimationNames.yellow, loop),
    };
    texture.state.timeScale = 0.5;
    this.animations.glow();
  }

  __getBackground({ name }) {
    const bg = this.createSprite(this.resources[name]);
    bg.anchor.set(.5, 1);
    bg.scale.set(gameMobileConfig.calculateScale(bg));
    this.setPosition(bg, gameMobileConfig.bgPosition);
    return bg;
  }

  /** shared  start*/
  setShadow() {
    const shadow = this.createSprite(this.resources[mobileBackgroundRssNames.shadow]);
    this.setPosition(shadow, { x: 0, y: gameMobileConfig.config.height - shadow.height - gameMobileConfig.mistake });
    this.container.addChild(shadow);
  }

  setHomeIcon() {
    const homeIcon = this.createSprite(this.resources[mobileBackgroundRssNames.homeIcon]);
    homeIcon.interactive = true;
    homeIcon.buttonMode = true;
    this.setPosition(homeIcon, { x: 15 + homeIcon.width / 2, y: 15 + homeIcon.width / 2 });
    homeIcon.anchor.set(.5);
    homeIcon.interactive = true;
    homeIcon.buttonMode = true;

    homeIcon.on('pointerdown', () => {
      soundService.playSingleClick();
      animationDrawer.click(homeIcon);
      iframeService.redirection()
    });
    this.container.addChild(homeIcon);
  }

  setLastResultUi({
    scale = 1.2,
    crystalW = 36,
    crystalScale = .18,
    parentWidth = gameMobileConfig.config.width,
    fullRectWidth = 278,
    observer = LAST_RESULTS,
  } = {}) {
    const container = this.createContainer('LAST_RESULT');
    const resultContainer = this.createContainer('RESULT_CONTAINER');
    const lastResultsOpacity = this.createGraphics()
      .drawRect(
        0,
        0,
        210,
        45,
      )
      .endFill();
    const lastResultsOpacityImage = this.createSprite(this.resources[mobileBackgroundRssNames.lastResultsOpacity]);
    lastResultsOpacityImage.scale.set(scale);
    lastResultsOpacity.addChild(lastResultsOpacityImage);
    const space = (parentWidth - lastResultsOpacity.width) / 2;
    this.setPosition(lastResultsOpacityImage, { x: space });
    const lastResultText = new this.PIXI.Text(`${getTranslation(translationKeys.LAST_RESULTS)}:`, {
      fontSize: 20,
      fill: 0xFFFFFF,
      align: 'right',
      fontFamily: FONT_LATO_NAME,
      fontWeight: 900,
    });
    this.setPosition(lastResultText, { x: space + 40, y: 8 });

    const rect = this.createGraphics()
      .drawRect(
        0,
        0,
        210,
        45,
      )
      .endFill();
    this.addSubscriber(
      observer.subscribe((crystals, { prev }) => {
        rect.children = [];
        crystals.forEach((crystalName, index) => {
          if (!this.resources[crystalTypes[crystalName]]) {
            return;
          }
          const container = this.createContainer('crystalName');
          const [star, starAnimation] = this.__createStar();
          const crystal = this.createSprite(this.resources[crystalTypes[crystalName]]);
          crystal.anchor.set(.5);
          this.setPosition(container, { x: 10 + index * (crystalW - 14), y: 17 });
          crystal.scale.set(crystalScale);
          if (!index && prev) {
            this.lastCrystal = {
              crystal,
              starAnimation,
            };
            this.lastCrystal.crystal.alpha = 0;
          }
          container.addChild(
            star,
            crystal,
          );
          rect.addChild(container);
        });
      }, true),
    );

    this.setPosition(rect, { x: space + fullRectWidth - 90, y: 4 });
    this.__calcEndAnimation({ x: space + fullRectWidth - 80, y: 21 });

    resultContainer.addChild(rect);
    container.addChild(
      lastResultsOpacity,
      lastResultText,
      resultContainer,
    );
    this.container.addChild(container);
  }

  setBalanceLastInfo({ isBonus = false } = {}) {
    const content = balanceLastWinDrawer.getContent({ sizes: { w: gameMobileConfig.width / 2 }, isBonus });
    this.setPosition(content, { x: 0, y: gameMobileConfig.config.height - content.height - gameMobileConfig.mistake });
    this.container.addChild(content);
  }

  addBlackMistake() {
    const rect = this.createGraphics()
      .beginFill(0x000000)
      .drawRect(
        0,
        gameMobileConfig.config.height - gameMobileConfig.mistake,
        gameMobileConfig.config.width,
        gameMobileConfig.mistake,
      )
      .endFill();
    this.container.addChild(rect);
  }

  destroy() {
    super.destroy();
    balanceLastWinDrawer.destroy();
  }

  /** shared end*/

  /** main scene  start*/
  setBackgroundFirstLayer() {
    const bg = this.__getBackground({ name: mobileBackgroundRssNames.backgroundFirstLayer });
    this.container.addChild(bg);
  }

  setBackgroundSecondLayer() {
    const bg = this.__getBackground({ name: mobileBackgroundRssNames.backgroundSecondLayer });
    this.container.addChild(bg);
  }

  setBackgroundPlantsAnimation() {
    const {
      [mobileBackgroundRssNames.plants]: plants,
    } = this.resources;
    const plantsTexture = this.createSpine(plants);
    this.setPosition(plantsTexture, gameMobileConfig.backgroundAnimationPosition);
    plantsTexture.state.setAnimation(0, 'animation', true);
    this.container.addChild(plantsTexture);
  }

  setBackgroundStoneAnimations() {
    const {
      [mobileBackgroundRssNames.stoneSpine]: stones,
    } = this.resources;
    const stonesTexture = this.createSpine(stones);
    stonesTexture.scale.set(gameMobileConfig.calculateScale(stonesTexture));
    this.setPosition(stonesTexture, gameMobileConfig.backgroundAnimationPosition);
    this.__setAnimations(stonesTexture);
    this.container.addChild(stonesTexture);
  }

  __calcEndAnimation({ x, y }) {
    if (!this.endPosition) {
      const { x: wx, y: wy } = gameMobileConfig.wheelPosition;
      this.endPosition = {
        x: x - wx,
        y: y - wy,
      };
    }
  }

  moveCrystal(crystalName, [wheel, container] = wheelDrawer.containerAndWheel) {
    return new Promise(resolve => {
      const content = this.createContainer('deleteElement');
      const [star, starAnimation] = this.__createStar();
      const crystal = this.createSprite(this.resources[crystalName]);
      starAnimation();
      crystal.anchor.set(.5);
      crystal.scale.set(.2);
      content.addChild(
        star,
        crystal,
      );
      this.__calcStartAnimation({ wheel });
      this.setPosition(content, this.startPosition);
      container.addChild(content);

      gsap.to(
        crystal,
        {
          duration: .4,
          pixi: {
            scale: 1,
          },
        },
      );
      gsap.to(
        crystal,
        {
          delay: .4,
          duration: .5,
          pixi: {
            scale: .2,
          },
        },
      );

      gsap.to(
        content,
        {
          delay: .4,
          duration: .5,
          ...this.endPosition,
          onComplete: () => {
            this.lastCrystal.starAnimation();
            this.lastCrystal.crystal.alpha = 1;
            container.removeChildAt(container.children.length - 1);
            resolve();
          },
        },
      );
    })
  }

  /** main scene  end*/

  /** pre bonus scene  start*/

  setPreBonusBackgroundFirstLayer() {
    const bg = this.__getBackground({ name: mobileBackgroundRssNames.preBonusBackgroundFirstLayer });
    this.container.addChild(bg);
  }

  setPreBonusBackgroundPlantsAnimation() {
    const {
      [mobileBackgroundRssNames.preBonusPlants]: plants,
    } = this.resources;
    const plantsTexture = this.createSpine(plants);

    this.setPosition(plantsTexture, gameMobileConfig.backgroundAnimationPosition);
    plantsTexture.state.setAnimation(0, 'animation', true);
    this.container.addChild(plantsTexture);
  }

  setPreBonusBackgroundSecondLayer() {
    const bg = this.__getBackground({ name: mobileBackgroundRssNames.preBonusBackgroundSecondLayer });
    this.container.addChild(bg);
  }

  setPreBonusCrystalLogo() {
    const logo = this.createSpine(this.resources[mobileBackgroundRssNames.logoSpine]);
    const scale = gameMobileConfig.config.height / gameMobileConfig.height * 1.1;
    logo.state.setAnimation(0, 'animation', true);
    logo.scale.set(scale);
    this.setPosition(logo, {
      ...gameMobileConfig.backgroundAnimationPosition,
      y: (gameMobileConfig.config.height - (gameMobileConfig.config.height - gameMobileConfig.height)) * scale,
    });
    this.container.addChild(logo);
  }

  setPreBonusStoneAnimations() {
    const {
      [mobileBackgroundRssNames.preBonusStoneSpine]: stones,
    } = this.resources;
    const stonesTexture = this.createSpine(stones);
    stonesTexture.scale.set(gameMobileConfig.calculateScale(stonesTexture));
    this.setPosition(stonesTexture, gameMobileConfig.backgroundAnimationPosition);
    this.__setAnimations(stonesTexture);
    this.container.addChild(stonesTexture);
  }

  /** pre bonus scene end*/

  /** bonus start  */

  setBonusBackgroundFirstLayer() {
    const bg = this.__getBackground({ name: mobileBackgroundRssNames.bonusBackgroundFirstLayer });
    this.container.addChild(bg);
  }

  setBonusBackgroundTreeLight() {
    const bonusTreeLight = this.createSpine(this.resources[mobileBackgroundRssNames.bonusTreeSpine]);
    this.setPosition(bonusTreeLight, gameMobileConfig.bgPosition);
    bonusTreeLight.scale.set(gameMobileConfig.calculateScale(bonusTreeLight));
    bonusTreeLight.state.setAnimation(0, 'animation', true);

    this.container.addChild(bonusTreeLight);
  }

  setBonusPlantsAnimation() {
    const {
      [mobileBackgroundRssNames.bonusPlants]: plants,
    } = this.resources;
    const plantsTexture = this.createSpine(plants);

    this.setPosition(plantsTexture, gameMobileConfig.backgroundAnimationPosition);
    plantsTexture.state.setAnimation(0, 'animation', true);
    this.container.addChild(plantsTexture);
  }

  setBonusStoneAnimations() {
    const {
      [mobileBackgroundRssNames.bonusStoneSpine]: stones,
    } = this.resources;
    const stonesTexture = this.createSpine(stones);
    stonesTexture.scale.set(gameMobileConfig.calculateScale(stonesTexture));
    this.setPosition(stonesTexture, gameMobileConfig.backgroundAnimationPosition);
    this.__setAnimations(stonesTexture);
    this.container.addChild(stonesTexture);
    return this;
  }

  /** bonus end  */

  async executeMainScene() {
    await this.loadResource();
    this.setBackgroundFirstLayer();
    this.setBackgroundPlantsAnimation();
    this.setBackgroundSecondLayer();
    this.setBackgroundStoneAnimations();
    this.setShadow();
    this.setHomeIcon();
    this.setLastResultUi();
    this.setBalanceLastInfo();
    this.addBlackMistake();
  }

  async executePreBonusScene() {
    await this.loadPreBonusResource();
    this.setPreBonusBackgroundFirstLayer();
    this.setPreBonusBackgroundPlantsAnimation();
    this.setPreBonusBackgroundSecondLayer();
    this.setPreBonusStoneAnimations();
    this.setShadow();
    this.setHomeIcon();
    this.setBalanceLastInfo();
    this.setPreBonusCrystalLogo();
    this.addBlackMistake();
  }

  async executeBonusScene() {
    await this.loadBonusResource();
    this.setBonusBackgroundFirstLayer();
    this.setBonusPlantsAnimation();
    this.setBonusBackgroundTreeLight();
    this.setBonusStoneAnimations();
    this.setHomeIcon();
    this.setShadow();
    this.setLastResultUi({ observer: LAST_RESULTS_BONUS });
    this.setBalanceLastInfo({ isBonus: true });
    this.addBlackMistake();
  }
}

export const backgroundMobileDrawer = new BackgroundMobileDrawer();
